import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { GovernanceBreadcrumbs, SectionCard, ToggleSwitch, btnPrimary, inputClass, selectClass } from '../components/shared';
import { aiGovernanceService, GovernanceSettings, PolicyListItem } from '../../../../components/api/aiGovernanceService';

const DEFAULT_SETTINGS: GovernanceSettings = {
  enforcePoliciesGlobally: true,
  defaultPolicyId: '',
  autoAssignNewResources: true,
  requireApprovalForOverrides: true,
  maxOverrideDurationDays: 30,
  notifyOnPolicyViolation: true,
  notifyOnHighRisk: true,
  policyDecisionRetentionDays: 365,
  enableRealTimeMonitoring: true,
  blockUnassignedResources: false,
  cascadeDepartmentPolicies: true,
  auditLogRetentionDays: 730,
  emailNotifications: '',
  slackWebhook: '',
};

export default function GovernanceSettingsScreen() {
  const [settings, setSettings] = useState<GovernanceSettings>(DEFAULT_SETTINGS);
  const [policies, setPolicies] = useState<PolicyListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [settingsRes, policiesRes] = await Promise.all([
          aiGovernanceService.getSettings(),
          aiGovernanceService.getPolicies({ page: 1, pageSize: 100 }),
        ]);
        setSettings({ ...DEFAULT_SETTINGS, ...settingsRes });
        setPolicies(policiesRes.items || []);
      } catch {
        setError('Failed to load settings.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const update = <K extends keyof GovernanceSettings>(key: K, value: GovernanceSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await aiGovernanceService.saveSettings(settings);
      setSettings({ ...DEFAULT_SETTINGS, ...saved });
      setSuccess('Settings saved.');
    } catch {
      setError('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-sm text-slate-500">Loading settings...</div>;
  }

  return (
    <div className="space-y-4">
      <GovernanceBreadcrumbs items={[{ label: 'AI Governance' }, { label: 'Settings' }]} />

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Governance Settings</h1>
          <p className="text-xs text-slate-500 mt-0.5">Configure platform-wide AI governance defaults and enforcement behavior.</p>
        </div>
        <button onClick={handleSave} disabled={saving} className={btnPrimary}><Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save Settings'}</button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">{error}</div>}
      {success && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs rounded-lg px-3 py-2">{success}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <SectionCard title="Global Enforcement" description="Platform-wide policy enforcement settings">
          <ToggleSwitch label="Enforce policies globally on all AI resources" checked={settings.enforcePoliciesGlobally} onChange={(v) => update('enforcePoliciesGlobally', v)} />
          <ToggleSwitch label="Auto-assign default policy to new resources" checked={settings.autoAssignNewResources} onChange={(v) => update('autoAssignNewResources', v)} />
          <ToggleSwitch label="Block resources without assigned policy" checked={settings.blockUnassignedResources} onChange={(v) => update('blockUnassignedResources', v)} />
          <ToggleSwitch label="Cascade department policies to child resources" checked={settings.cascadeDepartmentPolicies} onChange={(v) => update('cascadeDepartmentPolicies', v)} />
          <div className="mt-3">
            <label className="block text-[11px] font-medium text-slate-600 mb-1">Default Enterprise Policy</label>
            <select value={settings.defaultPolicyId} onChange={(e) => update('defaultPolicyId', e.target.value)} className={selectClass}>
              <option value="">Select default policy</option>
              {policies.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </SectionCard>

        <SectionCard title="Override Controls" description="Exception and override governance">
          <ToggleSwitch label="Require approval for all policy overrides" checked={settings.requireApprovalForOverrides} onChange={(v) => update('requireApprovalForOverrides', v)} />
          <div className="mt-3">
            <label className="block text-[11px] font-medium text-slate-600 mb-1">Maximum Override Duration (days)</label>
            <input type="number" value={settings.maxOverrideDurationDays} onChange={(e) => update('maxOverrideDurationDays', Number(e.target.value) || 0)} className={inputClass} />
          </div>
        </SectionCard>

        <SectionCard title="Notifications" description="Alert configuration for policy events">
          <ToggleSwitch label="Notify on policy violations" checked={settings.notifyOnPolicyViolation} onChange={(v) => update('notifyOnPolicyViolation', v)} />
          <ToggleSwitch label="Notify on high-risk events" checked={settings.notifyOnHighRisk} onChange={(v) => update('notifyOnHighRisk', v)} />
          <ToggleSwitch label="Enable real-time monitoring dashboard" checked={settings.enableRealTimeMonitoring} onChange={(v) => update('enableRealTimeMonitoring', v)} />
          <div className="mt-3 space-y-2">
            <div>
              <label className="block text-[11px] font-medium text-slate-600 mb-1">Notification Email</label>
              <input value={settings.emailNotifications} onChange={(e) => update('emailNotifications', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-slate-600 mb-1">Slack Webhook URL</label>
              <input value={settings.slackWebhook} onChange={(e) => update('slackWebhook', e.target.value)} placeholder="https://hooks.slack.com/..." className={inputClass} />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Data Retention" description="Audit log and compliance data retention">
          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-medium text-slate-600 mb-1">Policy Decision Retention (days)</label>
              <input type="number" value={settings.policyDecisionRetentionDays} onChange={(e) => update('policyDecisionRetentionDays', Number(e.target.value) || 0)} className={inputClass} />
            </div>
            <div>
              <label className="block text-[11px] font-medium text-slate-600 mb-1">Audit Log Retention (days)</label>
              <input type="number" value={settings.auditLogRetentionDays} onChange={(e) => update('auditLogRetentionDays', Number(e.target.value) || 0)} className={inputClass} />
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
